import { useRef, useEffect, useState } from 'react';
import { startOfMonth, endOfMonth } from 'date-fns';
import { getToday } from '@atlassian/jira-software-deployments-common-table/src/utils/dates.tsx';
import type { CalendarEventType, DatePickerProps } from '../../../../../common/types';
import { getRoundedDate } from '../../../../../common/utils/dates';

const isDateAfterMinMonth = (date: Date, minDate: Date): boolean => date > startOfMonth(minDate);

const isDateBeforeMaxMonth = (date: Date, maxDate: Date): boolean => date < endOfMonth(maxDate);

const isDateBeforeCurrentMonth = (date: Date): boolean => date < endOfMonth(new Date());

const isDateAfterCurrentMonth = (date: Date): boolean => date > startOfMonth(new Date());

export const shouldCalendarChange = (
	date: Date,
	{ disableFuture = false, disablePast = false, minDate, maxDate }: DatePickerProps,
): boolean =>
	(!minDate || isDateAfterMinMonth(date, minDate)) &&
	(!maxDate || isDateBeforeMaxMonth(date, maxDate)) &&
	(!disablePast || isDateAfterCurrentMonth(date)) &&
	(!disableFuture || isDateBeforeCurrentMonth(date));

export const useCalendar = (
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	selected: Date | void,
	{ disableFuture = false, disablePast = false, minDate, maxDate }: DatePickerProps,
) => {
	const [calendarDate, setCalendarDate] = useState(getToday());
	const isInitialRenderComplete = useRef(false);

	const setCalendar = (date: Date, type: CalendarEventType) => {
		if (
			(type !== 'prev' && type !== 'next') ||
			shouldCalendarChange(date, { disablePast, disableFuture, minDate, maxDate })
		) {
			setCalendarDate(
				getRoundedDate(date, {
					disablePast,
					disableFuture,
					minDate,
					maxDate,
				}),
			);
		}
	};

	useEffect(() => {
		if (selected !== undefined) {
			setCalendarDate(selected);
		}
	}, [selected]);

	useEffect(() => {
		// prevent this hook from running on initial render
		if (isInitialRenderComplete.current) {
			if (minDate !== undefined && minDate > calendarDate) {
				setCalendarDate(minDate);
			}
		} else {
			isInitialRenderComplete.current = true;
		}
	}, [calendarDate, minDate]);

	return { setCalendar, calendarDate };
};
